/**
 * v3 main screen — dock state, dismiss/open handlers, and derived visibility flags.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BackHandler, Keyboard } from 'react-native';
import { useAuthSheets } from '../shared/auth/authSheetContext';
import { MAIN_SCREEN_DOCK_MS, type SheetPhase } from '../shared/theme/studio/onboardingMotion';
import {
  androidMainScreenAuthBarDismissReserveBottom,
  androidMainScreenDockActive,
  isMainScreenAndroid,
} from '../shared/auth/onboardingMainScreenAndroidParity';

type ApplyMainPhase = (target: 0 | 1 | 2) => void;

export function useMainScreenDockController(applyMainPhase: ApplyMainPhase, insets_bottom: number) {
  const { dismiss, state: auth_sheet_state } = useAuthSheets();
  const is_android = isMainScreenAndroid();

  const [main_dock, setMainDock] = useState<0 | 1 | 2>(0);
  const [android_dock_form_mounted, setAndroidDockFormMounted] = useState(false);

  const sheet_phase: SheetPhase = main_dock === 0 ? 'idle' : main_dock === 1 ? 'login' : 'create';
  const is_idle_sheet = sheet_phase === 'idle';
  const android_dock_active = is_android ? androidMainScreenDockActive(main_dock, android_dock_form_mounted) : false;
  const dock_open = main_dock > 0;
  const form_mounted = is_android ? android_dock_form_mounted : main_dock > 0;
  const dock_backdrop_visible = is_android ? dock_open || android_dock_form_mounted : true;
  const auth_float_active = is_android ? android_dock_active : dock_open;

  const android_dismiss_reserve_bottom = useMemo(
    () => (is_android ? androidMainScreenAuthBarDismissReserveBottom(insets_bottom) : 0),
    [insets_bottom, is_android],
  );

  const onDismissMainAuth = useCallback(() => {
    Keyboard.dismiss();
    applyMainPhase(0);
    if (is_android) {
      setTimeout(() => {
        setMainDock(0);
        setAndroidDockFormMounted(false);
        dismiss();
      }, MAIN_SCREEN_DOCK_MS);
      return;
    }
    setMainDock(0);
    dismiss();
  }, [applyMainPhase, dismiss, is_android]);

  const openMainDock = useCallback(
    (phase: 1 | 2) => {
      if (is_android) {
        setAndroidDockFormMounted(true);
      }
      setMainDock(phase);
      applyMainPhase(phase);
    },
    [applyMainPhase, is_android],
  );

  const switchToRegister = useCallback(() => openMainDock(2), [openMainDock]);
  const switchToLogin = useCallback(() => openMainDock(1), [openMainDock]);

  useEffect(() => {
    if (auth_sheet_state == null) {
      return;
    }
    if (
      auth_sheet_state.kind === 'otp' ||
      auth_sheet_state.kind === 'reset' ||
      ((auth_sheet_state.kind === 'login' || auth_sheet_state.kind === 'register') &&
        (auth_sheet_state.presentation ?? 'default') !== 'mainScreen')
    ) {
      if (is_android && main_dock > 0) {
        applyMainPhase(0);
        const id = setTimeout(() => {
          setMainDock(0);
          setAndroidDockFormMounted(false);
        }, MAIN_SCREEN_DOCK_MS);
        return () => clearTimeout(id);
      }
      setMainDock(0);
      setAndroidDockFormMounted(false);
      applyMainPhase(0);
    }
  }, [auth_sheet_state, applyMainPhase, is_android, main_dock]);

  useEffect(() => {
    if (main_dock === 0) {
      return;
    }
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      onDismissMainAuth();
      return true;
    });
    return () => sub.remove();
  }, [main_dock, onDismissMainAuth]);

  return {
    main_dock,
    sheet_phase,
    is_idle_sheet,
    android_dock_active,
    dock_backdrop_visible,
    auth_float_active,
    form_mounted,
    android_dismiss_reserve_bottom,
    onDismissMainAuth,
    openMainDock,
    switchToLogin,
    switchToRegister,
  };
}
